/**
 * Pure sheet-activation identifier classification — deliberately dependency-free (no
 * `@ts3d-hoops/web-viewer` import) so it can be unit-tested without a live viewer/HOOPS runtime.
 *
 * Extracted from `sheet.helper.ts` while fixing the DWG blank-viewport bug — see
 * F:\viewer\Knowledge\mutable\investigations\dwg-auto-hide-investigation.md and
 * F:\viewer\Knowledge\stable\dwg-sheet-lifecycle.md.
 */

export const SHEET_ID_3D = -1211;

export type SheetActivationDecision =
    | { kind: "activate-sheet"; sheetId: number }
    | { kind: "deactivate-to-3d" }
    | { kind: "skip"; reason: string };

/**
 * Pure classification of what `activeSheetDataObser` should do for a given `id`.
 *
 * Durable invariant (see stable/dwg-sheet-lifecycle.md): `SHEET_ID_3D` is an explicit request to
 * leave sheet mode for model-space/3D content — it is never a fallback for an unrecognized value.
 * Any `id` that is neither `SHEET_ID_3D` nor a finite number (a UUID `viewId`, `null`, `undefined`,
 * `NaN`, a stale/merge identifier, ...) must be skipped, not treated as an implicit "go to 3D"
 * request — that conflation is what let a leaked UUID (or any other invalid value) trigger
 * `sheetManager.deactivateSheets()` and blank an already-correctly-rendered DWG.
 */
export function classifySheetActivation(
    id: unknown,
    hasUsable3DContent: boolean
): SheetActivationDecision {
    if (id === SHEET_ID_3D) {
        return hasUsable3DContent
            ? { kind: "deactivate-to-3d" }
            : {
                  kind: "skip",
                  reason: "SHEET_ID_3D requested but no usable 3D content exists (get3DNodes() empty) — refusing to deactivate the current sheet",
              };
    }
    if (typeof id === "number" && Number.isFinite(id)) {
        return { kind: "activate-sheet", sheetId: id };
    }
    return {
        kind: "skip",
        reason: `invalid sheet identifier (value=${String(
            id
        )}, type=${typeof id}) — not SHEET_ID_3D and not a finite number`,
    };
}
